// @flow
import * as React from 'react';
import {KeyToken, Identifier} from 'docs/components';
import {IDENTIFIER_MAP} from '../Identifier/identifierMap';
import type {IdentifierResourceType} from '../Identifier/identifierMap';
import type {CodeBlockHighlights} from '../CodeBlock/CodeBlock';

// We want to add custom tooltips to code generated by api-codegen.
// api-codegen uses prettier-poet under the hood and returns a raw
// string. We use a hack and do string parsing to replace certain
// known identifiers with React tooltips. The "right" way to do this
// would be to have api-codegen return a special AST instead of a string,
// and walk that, but that would require removing prettier-poet which would
// be a giant lift.

import {REPLACEMENT_KEY, REPLACEMENT_API_VERSION} from './constants';

// This matches the output of the identifier Markdoc function
const IDENTIFIER_RESOURCE_REGEX = /\$\$_RESOURCE_(.*?)\$\$/;

const replaceIdentifierWithTooltip = (generatedCode, lang) => {
  // We split to handle the case of multiple identifiers
  return generatedCode.split(/(\$\$_RESOURCE_.*?\$\$)/).map((fragment, i) => {
    const match = fragment.match(IDENTIFIER_RESOURCE_REGEX);
    if (!match) {
      return fragment;
    }

    let identifierTag = '{{RESOURCE_ID}}';
    const indentifierInfo = IDENTIFIER_MAP[match[1]];

    if (indentifierInfo) {
      // we know match[1] is a IdentifierResourceType since it was in the map,
      // casting to any is safe here
      const identifierType: IdentifierResourceType = (match[1]: any);
      identifierTag = (
        <Identifier
          key={`${indentifierInfo.id}-${i}`}
          type={identifierType}
          lang={lang}
          value={indentifierInfo.id}
        />
      );
    }

    return identifierTag;
  });
};

const addKeyTooltip = (
  generatedCode: Array<React.Node>,
  secretKey: string,
): React.Node => {
  const key = (index) => (
    <KeyToken key={index} type="secret" value={secretKey}>
      {secretKey}
    </KeyToken>
  );

  return generatedCode.flatMap((block) => {
    if (typeof block === 'string' && block.includes(REPLACEMENT_KEY)) {
      return block.split(REPLACEMENT_KEY).flatMap((code, i, arr) => {
        if (i < arr.length - 1) {
          return [code, key(i)];
        } else {
          return [code];
        }
      });
    } else {
      return [block];
    }
  });
};

export const addTooltipsToGeneratedCode = (
  generatedCode: ?string,
  lang: string,
  secretKey: string,
  apiVersion: string,
): React.Node => {
  if (!generatedCode) {
    return null;
  }

  const withApiVersion = generatedCode.replace(
    REPLACEMENT_API_VERSION,
    apiVersion,
  );
  const withIdentifier = replaceIdentifierWithTooltip(withApiVersion, lang);
  return addKeyTooltip(withIdentifier, secretKey);
};

export const getCodeBlockHighlightRange = (
  argsToHighlight: ?Array<string>,
  code: string | null,
): CodeBlockHighlights | null => {
  if (!argsToHighlight || !code) {
    return null;
  }

  // HACK (matv): This is a hack, but it probably supports 95% of use cases. The
  // "correct" way to do this would have printExampleRequest be smarter and be able to map
  // arguments to ranges inside of code generation.
  // That would be a very big lift. This tiny hack is probably better than having teams
  // hardcode raw snippets just to add highlighting for lines.
  const codeBlockHighlights = {
    add: [],
  };

  const lines = code.split('\n');
  argsToHighlight.forEach((argToHighlight) => {
    // Turn an arg like "payment_method_collection" to the regex
    // /payment[_-]?method[_-]?collection/i so it can match setPaymentMethodCollection (java),
    // payment-method-collection (stripe cli), etc
    const regex = new RegExp(argToHighlight.replace(/_/g, '[_-]?'), 'i');
    lines.forEach((line, index) => {
      if (regex.test(line)) {
        codeBlockHighlights.add.push({start: index + 1, end: index + 1});
      }
    });
  });

  return codeBlockHighlights;
};
